import { useMemo, useState } from "react";
import * as d3 from "d3";
import { useChartDimensions } from "./useChartDimensions";
import { NEUTRAL, categoricalColour } from "../../config/vizPalette";

/**
 * Reusable time-series chart: bars and/or lines over day / week / month /
 * year periods, with an optional right-hand axis, tooltip and annotation
 * markers.
 *
 * @param {Array<object>} data - Rows with a `period` ISO date ('YYYY-MM-DD', start of period)
 * @param {string} grain - day | week | month | year (sets bar width and tick format)
 * @param {Array<object>} series - [{ key, label, type: 'line'|'bar', axis: 'left'|'right', colour?, format? }]
 * @param {object} yLeft - { label, domain?, format? }
 * @param {object} yRight - Optional { label, domain?, format? }
 * @param {Array<object>} annotations - [{ date: ISO string, label }]
 * @param {number} height - Chart height in px (width follows the container)
 * @param {string} ariaLabel - Description for screen readers
 */

const INTERVALS = {
    day: d3.utcDay,
    week: d3.utcMonday, // matches DATE_TRUNC('week'), ISO weeks
    month: d3.utcMonth,
    year: d3.utcYear,
};

const TICK_FORMATS = {
    day: d3.utcFormat("%-d %b"),
    week: d3.utcFormat("%-d %b"),
    month: d3.utcFormat("%b %Y"),
    year: d3.utcFormat("%Y"),
};

const TOOLTIP_FORMATS = {
    day: d3.utcFormat("%-d %b %Y"),
    week: (date) => `Week of ${d3.utcFormat("%-d %b %Y")(date)}`,
    month: d3.utcFormat("%B %Y"),
    year: d3.utcFormat("%Y"),
};

const defaultFormat = (value) => (value == null ? "–" : d3.format(",.4~r")(value));

const parsePeriod = (period) => new Date(`${period.slice(0, 10)}T00:00:00Z`);

const yScaleFor = (axis, rows, keys, boundedHeight) => {
    const max = d3.max(rows, (row) => d3.max(keys, (key) => row[key])) || 1;
    return d3
        .scaleLinear()
        .domain(axis?.domain || [0, max * 1.1])
        .range([boundedHeight, 0])
        .nice();
};

const AxisLeft = ({ scale, format, label, dimensions }) => (
    <g>
        {scale.ticks(5).map((tick) => (
            <g key={tick} transform={`translate(0, ${scale(tick)})`}>
                <line x2={dimensions.boundedWidth} stroke="#e5e7eb" />
                <text x={-8} dy="0.32em" textAnchor="end" fontSize={12} fill="#6b7280">
                    {format(tick)}
                </text>
            </g>
        ))}
        {label && (
            <text
                transform={`translate(${-dimensions.marginLeft + 14}, ${dimensions.boundedHeight / 2}) rotate(-90)`}
                textAnchor="middle"
                fontSize={12}
                fill="#6b7280"
            >
                {label}
            </text>
        )}
    </g>
);

const AxisRight = ({ scale, format, label, dimensions }) => (
    <g transform={`translate(${dimensions.boundedWidth}, 0)`}>
        {scale.ticks(5).map((tick) => (
            <text key={tick} x={8} y={scale(tick)} dy="0.32em" fontSize={12} fill="#6b7280">
                {format(tick)}
            </text>
        ))}
        {label && (
            <text
                transform={`translate(${dimensions.marginRight - 10}, ${dimensions.boundedHeight / 2}) rotate(90)`}
                textAnchor="middle"
                fontSize={12}
                fill="#6b7280"
            >
                {label}
            </text>
        )}
    </g>
);

const ChartBody = ({ rows, grain, coloured, yLeft, yRight, annotations, dimensions, ariaLabel }) => {
    const [hoverIndex, setHoverIndex] = useState(null);

    const interval = INTERVALS[grain] || d3.utcMonth;
    const first = rows[0]._start;
    const end = interval.offset(rows[rows.length - 1]._start, 1);
    const x = d3.scaleUtc().domain([first, end]).range([0, dimensions.boundedWidth]);
    const centre = (row) => (x(row._start) + x(interval.offset(row._start, 1))) / 2;

    const leftKeys = coloured.filter((s) => s.axis === "left").map((s) => s.key);
    const rightKeys = coloured.filter((s) => s.axis === "right").map((s) => s.key);
    const yL = yScaleFor(yLeft, rows, leftKeys, dimensions.boundedHeight);
    const yR = yRight ? yScaleFor(yRight, rows, rightKeys, dimensions.boundedHeight) : null;
    const scaleFor = (s) => (s.axis === "right" && yR ? yR : yL);

    const bars = coloured.filter((s) => s.type === "bar");
    const lines = coloured.filter((s) => s.type === "line");
    const barSlot = x(interval.offset(first, 1)) - x(first);
    const barGap = barSlot > 6 ? Math.min(barSlot * 0.2, 6) : 0;
    const barWidth = Math.max((barSlot - barGap) / Math.max(bars.length, 1), 0.5);

    const xTicks = x.ticks(Math.max(2, Math.floor(dimensions.boundedWidth / 90)));
    const tickFormat = TICK_FORMATS[grain] || TICK_FORMATS.month;
    const centres = rows.map(centre);

    const onPointerMove = (event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const index = d3.bisectCenter(centres, event.clientX - bounds.left);
        setHoverIndex(Math.max(0, Math.min(rows.length - 1, index)));
    };

    const hovered = hoverIndex != null ? rows[hoverIndex] : null;
    const tooltipLeft = hovered ? dimensions.marginLeft + centres[hoverIndex] : 0;
    const tooltipOnLeft = hovered && centres[hoverIndex] > dimensions.boundedWidth / 2;

    return (
        <>
            <svg width={dimensions.width} height={dimensions.height} role="img" aria-label={ariaLabel}>
                <g transform={`translate(${dimensions.marginLeft}, ${dimensions.marginTop})`}>
                    <AxisLeft
                        scale={yL}
                        format={yLeft.format || defaultFormat}
                        label={yLeft.label}
                        dimensions={dimensions}
                    />
                    {yR && (
                        <AxisRight
                            scale={yR}
                            format={yRight.format || defaultFormat}
                            label={yRight.label}
                            dimensions={dimensions}
                        />
                    )}

                    {bars.map((s, barIndex) => (
                        <g key={s.key}>
                            {rows.map((row) =>
                                row[s.key] ? (
                                    <rect
                                        key={row.period}
                                        x={x(row._start) + barGap / 2 + barIndex * barWidth}
                                        y={scaleFor(s)(row[s.key])}
                                        width={barWidth}
                                        height={Math.max(dimensions.boundedHeight - scaleFor(s)(row[s.key]), 0)}
                                        fill={s.colour}
                                        opacity={0.85}
                                    />
                                ) : null
                            )}
                        </g>
                    ))}

                    {lines.map((s) => {
                        const path = d3
                            .line()
                            .defined((row) => row[s.key] != null)
                            .x(centre)
                            .y((row) => scaleFor(s)(row[s.key]))(rows);
                        return <path key={s.key} d={path} fill="none" stroke={s.colour} strokeWidth={2.5} />;
                    })}

                    {annotations
                        .filter((a) => a.date)
                        .map((a) => {
                            const ax = x(new Date(a.date));
                            if (ax < 0 || ax > dimensions.boundedWidth) return null;
                            return (
                                <g key={a.label} transform={`translate(${ax}, 0)`}>
                                    <line y2={dimensions.boundedHeight} stroke={NEUTRAL} strokeDasharray="4 3" />
                                    <text
                                        x={ax > dimensions.boundedWidth / 2 ? -4 : 4}
                                        y={10}
                                        textAnchor={ax > dimensions.boundedWidth / 2 ? "end" : "start"}
                                        fontSize={12}
                                        fill="#6b7280"
                                    >
                                        {a.label}
                                    </text>
                                </g>
                            );
                        })}

                    <g transform={`translate(0, ${dimensions.boundedHeight})`}>
                        <line x2={dimensions.boundedWidth} stroke="#9ca3af" />
                        {xTicks.map((tick) => (
                            <g key={tick.toISOString()} transform={`translate(${x(tick)}, 0)`}>
                                <line y2={5} stroke="#9ca3af" />
                                <text y={20} textAnchor="middle" fontSize={12} fill="#6b7280">
                                    {tickFormat(tick)}
                                </text>
                            </g>
                        ))}
                    </g>

                    {hovered && (
                        <line
                            x1={centres[hoverIndex]}
                            x2={centres[hoverIndex]}
                            y2={dimensions.boundedHeight}
                            stroke="#374151"
                            strokeOpacity={0.4}
                        />
                    )}
                    {hovered &&
                        lines.map((s) =>
                            hovered[s.key] != null ? (
                                <circle
                                    key={s.key}
                                    cx={centres[hoverIndex]}
                                    cy={scaleFor(s)(hovered[s.key])}
                                    r={4}
                                    fill={s.colour}
                                    stroke="white"
                                    strokeWidth={1.5}
                                />
                            ) : null
                        )}

                    <rect
                        width={dimensions.boundedWidth}
                        height={dimensions.boundedHeight}
                        fill="transparent"
                        style={{ touchAction: "pan-y" }}
                        onPointerMove={onPointerMove}
                        onPointerDown={onPointerMove}
                        onPointerLeave={() => setHoverIndex(null)}
                    />
                </g>
            </svg>

            {hovered && (
                <div
                    className="pointer-events-none absolute top-2 z-10 min-w-[10rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow"
                    style={
                        tooltipOnLeft
                            ? { right: dimensions.width - tooltipLeft + 8 }
                            : { left: tooltipLeft + 8 }
                    }
                >
                    <div className="mb-1 font-semibold text-gray-800">
                        {(TOOLTIP_FORMATS[grain] || TOOLTIP_FORMATS.month)(hovered._start)}
                    </div>
                    {coloured.map((s) => (
                        <div key={s.key} className="flex items-center justify-between gap-3 text-gray-700">
                            <span className="inline-flex items-center gap-2">
                                <span
                                    aria-hidden="true"
                                    className="inline-block h-2.5 w-2.5 rounded-full"
                                    style={{ backgroundColor: s.colour }}
                                />
                                {s.label}
                            </span>
                            <span className="font-mono">{(s.format || defaultFormat)(hovered[s.key])}</span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

const Legend = ({ coloured, annotations, dualAxis }) => (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-500">
        {coloured.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-2">
                <span
                    aria-hidden="true"
                    className={s.type === "bar" ? "inline-block h-3 w-3" : "inline-block h-1 w-4 rounded-full"}
                    style={{ backgroundColor: s.colour }}
                />
                {s.label}
                {dualAxis && <span className="text-gray-400">({s.axis === "right" ? "right" : "left"} axis)</span>}
            </span>
        ))}
        {annotations
            .filter((a) => a.date)
            .map((a) => (
                <span key={a.label} className="inline-flex items-center gap-2">
                    <span
                        aria-hidden="true"
                        className="inline-block h-4 w-0 border-l-2 border-dashed"
                        style={{ borderColor: NEUTRAL }}
                    />
                    {a.label}
                </span>
            ))}
    </div>
);

export const TimeSeriesChart = ({
    data = [],
    grain = "month",
    series = [],
    yLeft = {},
    yRight = null,
    annotations = [],
    height = 320,
    ariaLabel = "Time series chart",
}) => {
    // The measured element must stay mounted: useChartDimensions observes it once
    const [ref, dimensions] = useChartDimensions({
        height,
        marginTop: 16,
        marginRight: yRight ? 56 : 16,
        marginBottom: 32,
        marginLeft: 56,
    });
    const coloured = series.map((s, i) => ({ axis: "left", type: "line", colour: categoricalColour(i), ...s }));
    const rows = useMemo(
        () => data.map((row) => ({ ...row, _start: parsePeriod(row.period) })),
        [data]
    );

    return (
        <div>
            <div ref={ref} className="relative w-full" style={{ height }}>
                {rows.length > 0 && dimensions.boundedWidth > 0 && (
                    <ChartBody
                        rows={rows}
                        grain={grain}
                        coloured={coloured}
                        yLeft={yLeft}
                        yRight={yRight}
                        annotations={annotations}
                        dimensions={dimensions}
                        ariaLabel={ariaLabel}
                    />
                )}
            </div>
            <Legend coloured={coloured} annotations={annotations} dualAxis={Boolean(yRight)} />
        </div>
    );
};
